import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { EventCard } from '../components/events/EventCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Search, Filter, X, Music, Mic2, Trophy, Heart, Sparkles, PartyPopper, Award, History } from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const categories = [
  { id: 'all', name: 'All Events', icon: Filter },
  { id: 'music', name: 'Music', icon: Music },
  { id: 'comedy', name: 'Comedy', icon: Mic2 },
  { id: 'sports', name: 'Sports', icon: Trophy },
  { id: 'wedding', name: 'Wedding', icon: Heart },
  { id: 'nightlife', name: 'Nightlife', icon: Sparkles },
  { id: 'festivals', name: 'Festivals', icon: PartyPopper },
  { id: 'awards', name: 'Awards', icon: Award },
];

const cities = ['Lagos', 'Abuja', 'Port Harcourt', 'Ibadan', 'Kano', 'Accra', 'Nairobi'];

export default function Events() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || 'all');

  // Scroll to top when component mounts or category changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (selectedCity && selectedCity !== 'all') {
        params.append('city', selectedCity);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const response = await axios.get(`${API_URL}/api/events?${params.toString()}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [selectedCategory, selectedCity]);

  useEffect(() => {
    // Update URL params
    const params = new URLSearchParams();
    if (selectedCategory && selectedCategory !== 'all') {
      params.set('category', selectedCategory);
    }
    if (selectedCity && selectedCity !== 'all') {
      params.set('city', selectedCity);
    }
    if (searchQuery) {
      params.set('search', searchQuery);
    }
    setSearchParams(params);
  }, [selectedCategory, selectedCity, searchQuery, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEvents();
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedCity('all');
    setSearchQuery('');
  };

  const hasFilters = selectedCategory !== 'all' || selectedCity !== 'all' || searchQuery;

  return (
    <Layout>
      <div className="min-h-[calc(100vh-4rem)] bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {/* Header */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-foreground">Discover Events</h1>
              <p className="text-muted-foreground">Find your next unforgettable experience</p>
            </div>
            <Link to="/events/past">
              <Button variant="outline" className="gap-2" data-testid="past-events-link">
                <History className="w-4 h-4" />
                Past Events
              </Button>
            </Link>
          </div>

          {/* Search & Filters */}
          <div className="space-y-4 mb-8">
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search events, artists, venues..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 rounded-xl bg-card"
                  data-testid="events-search-input"
                />
              </div>
              <Button type="submit" className="h-12 px-6 rounded-xl" data-testid="events-search-btn">
                Search
              </Button>
            </form>

          {/* Category Pills */}
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const Icon = category.icon;
              const isActive = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                  }`}
                  data-testid={`category-filter-${category.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {category.name}
                </button>
              );
            })}
          </div>

          {/* City Filter & Active Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger className="w-[180px] h-10 rounded-xl" data-testid="city-filter">
                <SelectValue placeholder="Select city" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground hover:text-foreground"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Clear filters
              </Button>
            )}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-card border border-border overflow-hidden">
                <div className="aspect-[16/10] skeleton" />
                <div className="p-4 space-y-3">
                  <div className="h-6 skeleton rounded w-3/4" />
                  <div className="h-4 skeleton rounded w-1/2" />
                  <div className="h-4 skeleton rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : events.length > 0 ? (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              {events.length} event{events.length !== 1 ? 's' : ''} found
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((event) => (
                <EventCard key={event.event_id} event={event} />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No events found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search or filters to find what you're looking for.
            </p>
            <Button variant="outline" onClick={clearFilters} data-testid="no-results-clear-btn">
              Clear all filters
            </Button>
          </div>
        )}
        </div>
      </div>
    </Layout>
  );
}
